const PREFIX = '[LOGIN_REDIRECT_TRACE]';
const JWT_STORAGE_KEY = 'EnthiosHub_jwt';

export type LoginRedirectTraceEntry = {
  at: string;
  event: string;
  [key: string]: unknown;
};

export function loginRedirectTraceEnabled(): boolean {
  return process.env.NEXT_PUBLIC_LOGIN_REDIRECT_TRACE === 'true';
}

function pushTrace(entry: { event: string } & Record<string, unknown>): void {
  if (!loginRedirectTraceEnabled()) return;

  const line: LoginRedirectTraceEntry = { at: new Date().toISOString(), ...entry };
  console.warn(PREFIX, line);

  if (typeof window !== 'undefined') {
    const w = window as Window & { __LOGIN_REDIRECT_TRACE__?: LoginRedirectTraceEntry[] };
    if (!w.__LOGIN_REDIRECT_TRACE__) {
      w.__LOGIN_REDIRECT_TRACE__ = [];
    }
    w.__LOGIN_REDIRECT_TRACE__.push(line);
  }
}

export function traceLogin(event: string, payload?: Record<string, unknown>): void {
  pushTrace(payload ? { event, ...payload } : { event });
}

function responseHasToken(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const body = payload as Record<string, unknown>;
  const nested = body.data && typeof body.data === 'object' ? (body.data as Record<string, unknown>) : null;
  const candidates = [
    body.token,
    body.access_token,
    body.jwt,
    nested?.token,
    nested?.access_token,
    nested?.jwt,
  ];
  return candidates.some((value) => typeof value === 'string' && value.trim().length > 20);
}

export function traceTokenSaved(source: string, payload: unknown): void {
  if (!loginRedirectTraceEnabled()) return;

  const stored =
    typeof window !== 'undefined' ? localStorage.getItem(JWT_STORAGE_KEY) : null;

  traceLogin('TOKEN SAVED', {
    source,
    extractedFromResponse: responseHasToken(payload),
    localStoragePresent: Boolean(stored),
    cookiePresent:
      typeof document !== 'undefined' && document.cookie.includes(`${JWT_STORAGE_KEY}=`),
    tokenLength: stored?.length ?? 0,
  });
}

export function traceAuthState(
  source: string,
  state: {
    user?: { id?: string; email?: string; role?: string } | null;
    isAuthenticated?: boolean;
    authChecking?: boolean;
  }
): void {
  traceLogin('AUTH STATE', {
    source,
    isAuthenticated: state.isAuthenticated ?? false,
    authChecking: state.authChecking ?? false,
    userId: state.user?.id ?? null,
    email: state.user?.email ?? null,
    role: state.user?.role ?? null,
  });
}

export function traceMeResponse(
  endpoint: '/auth/me' | '/admin/me',
  status: number,
  data: unknown
): void {
  const body = data as { data?: unknown; user?: unknown } | null;
  let user: unknown = null;

  if (body?.data && typeof body.data === 'object' && body.data !== null && 'user' in body.data) {
    user = (body.data as { user?: unknown }).user ?? null;
  } else if (body?.data !== undefined) {
    user = body.data;
  } else if (body?.user !== undefined) {
    user = body.user;
  }

  const roleUser = user as { role?: string; user_type?: string } | null;

  traceLogin('ME RESPONSE', {
    endpoint,
    status,
    hasUser: Boolean(user),
    dataIsNull: user === null,
    role: roleUser?.role ?? roleUser?.user_type ?? null,
  });
}

export function getLoginRedirectTrace(): LoginRedirectTraceEntry[] {
  if (typeof window === 'undefined') return [];
  const w = window as Window & { __LOGIN_REDIRECT_TRACE__?: LoginRedirectTraceEntry[] };
  return w.__LOGIN_REDIRECT_TRACE__ ?? [];
}

export function resetLoginRedirectTrace(): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { __LOGIN_REDIRECT_TRACE__?: LoginRedirectTraceEntry[] };
  w.__LOGIN_REDIRECT_TRACE__ = [];
}
